/* ============================================================
   field.js — an iron-filing vector field on a 2D canvas.

   Every filing points along a slow drifting flow until a pointer
   comes near, at which point it swings round to point *at* the
   pointer and brightens. The studio's first app is called Pulled;
   the hero is literally a magnet.

   Kept deliberately dependency-free and allocation-free per frame:
   the filings live in flat typed arrays and the draw pass buckets
   them by brightness so the whole field is six stroke() calls.
   ============================================================ */

const TAU = Math.PI * 2;
const BUCKETS = 6;

/** Shortest-path angular interpolation (so filings never spin the long way). */
function lerpAngle(a, b, t) {
  let d = ((b - a + Math.PI) % TAU) - Math.PI;
  if (d < -Math.PI) d += TAU;
  return a + d * t;
}

export function createField(canvas, options = {}) {
  const {
    spacing = 34,
    radius = 270,
    idleSpeed = 0.00016,
    getColor = () => "237,233,225",
  } = options;

  const ctx = canvas.getContext("2d", { alpha: true });

  let dpr = 1;
  let w = 0;
  let h = 0;
  let cols = 0;
  let rows = 0;
  let count = 0;

  let px = -9999;
  let py = -9999;
  let tx = -9999;
  let ty = -9999;
  let hasPointer = false;

  /** Expanding shockwave from a click: [radius, life]. */
  let waveR = 0;
  let waveLife = 0;

  let xs = new Float32Array(0);
  let ys = new Float32Array(0);
  let ang = new Float32Array(0);
  let seed = new Float32Array(0);

  let raf = 0;
  let running = false;
  let intensity = 1; // faded out by the page as the hero leaves

  const paths = new Array(BUCKETS);

  function layout() {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvas.getBoundingClientRect();
    w = Math.max(1, Math.round(rect.width));
    h = Math.max(1, Math.round(rect.height));

    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    cols = Math.ceil(w / spacing) + 1;
    rows = Math.ceil(h / spacing) + 1;
    count = cols * rows;

    xs = new Float32Array(count);
    ys = new Float32Array(count);
    ang = new Float32Array(count);
    seed = new Float32Array(count);

    // Offset every other row by half a step — a hex-ish lattice reads as a
    // field rather than as a spreadsheet.
    let i = 0;
    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < cols; c += 1) {
        xs[i] = c * spacing + (r % 2 ? spacing / 2 : 0);
        ys[i] = r * spacing;
        ang[i] = 0;
        seed[i] = Math.random() * TAU;
        i += 1;
      }
    }
  }

  function frame(now) {
    raf = requestAnimationFrame(frame);
    if (!running) return;

    // Pointer easing — the field lags the cursor slightly, which is what
    // makes it feel like it has mass.
    px += (tx - px) * 0.12;
    py += (ty - py) * 0.12;

    if (waveLife > 0) {
      waveR += 16;
      waveLife -= 0.016;
    }

    ctx.clearRect(0, 0, w, h);
    if (intensity <= 0.005) return;

    for (let b = 0; b < BUCKETS; b += 1) paths[b] = new Path2D();

    const t = now * idleSpeed;
    const r2 = radius * radius;

    for (let i = 0; i < count; i += 1) {
      const x = xs[i];
      const y = ys[i];

      // --- idle flow: two cheap sine waves standing in for noise ---
      let target =
        Math.sin(x * 0.0042 + t * 1.7 + seed[i] * 0.15) * 1.5 +
        Math.cos(y * 0.0055 - t * 1.2) * 1.5;

      let pull = 0;

      if (hasPointer) {
        const dx = px - x;
        const dy = py - y;
        const d2 = dx * dx + dy * dy;
        if (d2 < r2 * 2.6) {
          pull = 1 / (1 + d2 / r2);
          target = lerpAngle(target, Math.atan2(dy, dx), Math.min(1, pull * 1.35));
        }
      }

      if (waveLife > 0) {
        const dx = px - x;
        const dy = py - y;
        const d = Math.sqrt(dx * dx + dy * dy);
        const band = 1 - Math.min(1, Math.abs(d - waveR) / 130);
        if (band > 0) {
          const ring = band * waveLife;
          pull = Math.max(pull, ring);
          target = lerpAngle(target, Math.atan2(dy, dx) + Math.PI / 2, ring * 0.9);
        }
      }

      const a = lerpAngle(ang[i], target, 0.09 + pull * 0.16);
      ang[i] = a;

      const len = (6.5 + pull * 15) * 0.5;
      const cos = Math.cos(a) * len;
      const sin = Math.sin(a) * len;

      const brightness = Math.min(1, 0.14 + pull * 1.05) * intensity;
      const b = Math.min(BUCKETS - 1, (brightness * BUCKETS) | 0);

      const p = paths[b];
      p.moveTo(x - cos, y - sin);
      p.lineTo(x + cos, y + sin);
    }

    const rgb = getColor();
    ctx.lineCap = "round";
    for (let b = 0; b < BUCKETS; b += 1) {
      const alpha = ((b + 0.6) / BUCKETS) * 0.62 * intensity;
      if (alpha < 0.004) continue;
      ctx.strokeStyle = `rgba(${rgb},${alpha.toFixed(3)})`;
      ctx.lineWidth = b >= BUCKETS - 2 ? 1.5 : 1;
      ctx.stroke(paths[b]);
    }
  }

  /* ---------- listeners ---------- */

  const onMove = (e) => {
    const rect = canvas.getBoundingClientRect();
    tx = e.clientX - rect.left;
    ty = e.clientY - rect.top;
    if (!hasPointer) {
      px = tx;
      py = ty;
      hasPointer = true;
    }
  };

  const onLeave = () => {
    hasPointer = false;
    tx = -9999;
    ty = -9999;
  };

  const onDown = () => {
    if (!hasPointer) return;
    waveR = 0;
    waveLife = 1;
  };

  const onResize = () => layout();

  layout();
  running = true;
  raf = requestAnimationFrame(frame);

  window.addEventListener("pointermove", onMove, { passive: true });
  window.addEventListener("pointerdown", onDown, { passive: true });
  window.addEventListener("pointerleave", onLeave);
  window.addEventListener("resize", onResize);

  return {
    /** 0 → invisible, 1 → full strength. The hero fades it out on scroll. */
    setIntensity(v) {
      intensity = Math.max(0, Math.min(1, v));
      running = intensity > 0.005 || running;
    },
    pause() {
      running = false;
    },
    resume() {
      running = true;
    },
    destroy() {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("resize", onResize);
    },
  };
}
